// Nápověda trenéra: ikona hlavy bota v horní liště + okno přes celou obrazovku (na mobilu spodní panel).
// Odpovídá jen z aktuálního Manuálu trenéra a přepisů modulů školení (API /api/dokument-chat).
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Bot, Send, X, RefreshCw } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { DocLink } from "../pages/trener/DokumentDetail.jsx";

const NAPADY = ["Co když onemocním?", "Storno klientky", "Jak zapíšu úraz?", "Ceník a slevy", "Slack a VT GYM", "Délka lekce"];

// Krátkodobá paměť konverzace: po zavření okna zůstane 20 minut, pak se začne znovu.
const PAMET_KEY = "fw40_napoveda_chat";
const PAMET_MS = 20 * 60 * 1000;
function nactiPamet() {
  try {
    const o = JSON.parse(localStorage.getItem(PAMET_KEY) || "null");
    if (o && Array.isArray(o.messages) && Date.now() - o.t < PAMET_MS) return o.messages;
  } catch { /* bez paměti */ }
  return [];
}
function ulozPamet(messages) {
  try {
    if (messages.length) localStorage.setItem(PAMET_KEY, JSON.stringify({ t: Date.now(), messages: messages.slice(-20) }));
    else localStorage.removeItem(PAMET_KEY);
  } catch { /* bez paměti */ }
}

function NapovedaOkno({ onClose }) {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState(nactiPamet); // {role, content, sources?}
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);
  useEffect(() => { ulozPamet(messages); }, [messages]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const send = async (text) => {
    const q = String(text ?? input).trim();
    if (!q || busy) return;
    const history = messages.map(({ role, content }) => ({ role, content }));
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    try {
      const r = await apiFetch("/api/dokument-chat", { method: "POST", body: JSON.stringify({ question: q, history }) });
      const j = await r.json().catch(() => ({}));
      setMessages((m) => [...m, { role: "assistant", content: j.answer || "Nepodařilo se odpovědět. Zkus to prosím znovu.", sources: j.sources || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `Chyba: ${e.message}`, sources: [] }]);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-[#1d1033]/70 backdrop-blur-sm" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} role="dialog" aria-modal="true" aria-label="Nápověda trenéra">
      <div className="absolute inset-x-0 bottom-0 top-10 flex flex-col overflow-hidden rounded-t-3xl bg-[#faf7ff] shadow-2xl lg:inset-7 lg:rounded-3xl">
        <div className="flex shrink-0 items-center gap-3 bg-gradient-to-r from-[#2d1b4e] to-[#6b4a9c] px-4 py-3.5 text-white lg:px-6 lg:py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/15"><Bot className="h-6 w-6" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-serif text-lg font-bold leading-tight lg:text-xl">Nápověda trenéra</div>
            <div className="truncate text-xs text-white/75">Odpovídá jen z aktuálního Manuálu trenéra</div>
          </div>
          {messages.length > 0 && (
            <button onClick={() => setMessages([])} disabled={busy} title="Začít novou konverzaci" className="flex h-10 shrink-0 items-center gap-1.5 rounded-xl bg-white/15 px-3 text-sm font-semibold hover:bg-white/25 disabled:opacity-50">
              <RefreshCw className="h-4 w-4" /> <span className="hidden sm:inline">Nová konverzace</span>
            </button>
          )}
          <button onClick={onClose} aria-label="Zavřít" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 hover:bg-white/25"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex min-h-0 flex-1">
          <aside className="hidden w-64 shrink-0 overflow-y-auto border-r border-[#eadff7] bg-white p-4 lg:block">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Časté otázky</div>
            <div className="space-y-1.5">
              {NAPADY.map((n) => (
                <button key={n} onClick={() => send(n)} disabled={busy} className="block w-full rounded-xl border border-[#efe7fa] bg-[#faf7ff] px-3 py-2.5 text-left text-sm text-[#3a2a5a] hover:border-[#cdb9ea] disabled:opacity-50">{n}</button>
              ))}
            </div>
            <div className="mb-1 mt-5 text-[11px] font-bold uppercase tracking-wider text-slate-500">Zdroje</div>
            <div className="text-xs leading-relaxed text-slate-500">Manuál trenéra a přepisy jeho 14 modulů. Co tam není, to ti nenapíšu, zeptej se Head Coache.</div>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-5 lg:px-14 lg:py-7">
              {messages.length === 0 && (
                <div className="py-10 text-center">
                  <Bot className="mx-auto mb-3 h-10 w-10 text-brand-300" />
                  <div className="text-lg font-semibold text-slate-700">Zeptej se na cokoliv z manuálu</div>
                  <div className="mt-1 text-sm text-slate-500">Pravidla, zkušební trénink, organizace, Kniha úrazů, aplikace…</div>
                  <div className="mt-5 flex flex-wrap justify-center gap-2 lg:hidden">
                    {NAPADY.slice(0, 4).map((n) => <button key={n} onClick={() => send(n)} className="rounded-full border border-[#d9cbef] bg-white px-3 py-1.5 text-sm text-[#4b2d7f]">{n}</button>)}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[92%] break-words rounded-3xl px-4 py-3 text-[15px] lg:max-w-[72%] ${m.role === "user" ? "whitespace-pre-wrap rounded-br-md bg-[#7b5cb0] text-white" : "rounded-bl-md border border-[#e5dcf5] bg-white text-slate-800"}`}>
                    {m.role === "assistant" ? (
                      <>
                        <div className="prose prose-sm max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5 prose-strong:text-slate-900">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: DocLink }}>{m.content}</ReactMarkdown>
                        </div>
                        {m.sources?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {m.sources.slice(0, 3).map((s) => <span key={s} className="rounded-lg bg-[#efe6fb] px-2 py-0.5 text-xs font-semibold text-[#4b2d7f]">{s}</span>)}
                          </div>
                        )}
                      </>
                    ) : m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  {/* 19.10.2026 (hlášení #168): místo "Hledám v manuálu" tři tečky, jako když někdo píše. */}
                  <div className="inline-flex items-center gap-1.5 rounded-3xl rounded-bl-md border border-[#e5dcf5] bg-white px-4 py-4" role="status" aria-label="Nápověda píše odpověď">
                    {[0, 150, 300].map((d) => (
                      <span key={d} className="h-2 w-2 rounded-full bg-[#7b5cb0] animate-bounce" style={{ animationDelay: `${d}ms`, animationDuration: "1s" }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex shrink-0 items-end gap-2 border-t border-[#eadff7] bg-[#faf7ff] px-4 py-3 lg:px-14 lg:py-4">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Napiš otázku…"
                rows={1}
                className="max-h-28 min-h-[48px] flex-1 resize-none rounded-2xl border-2 border-[#d9cbef] bg-white px-4 py-3 text-[15px] focus:border-[#7b5cb0] focus:outline-none"
              />
              <button onClick={() => send()} disabled={busy || !input.trim()} className="flex h-12 shrink-0 items-center gap-2 rounded-2xl bg-[#7b5cb0] px-5 font-semibold text-white hover:bg-[#6b4a9c] disabled:opacity-40" title="Odeslat">
                <Send className="h-4 w-4" /> <span className="hidden sm:inline">Poslat</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function NapovedaTrenera() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className="flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl hover:bg-slate-100"
        aria-label="Nápověda trenéra"
        title="Nápověda trenéra (odpovídá z manuálu)"
        onClick={() => setOpen(true)}
      >
        <Bot className="h-6 w-6 text-slate-600" />
      </button>
      {open && <NapovedaOkno onClose={() => setOpen(false)} />}
    </>
  );
}
